/* eslint-disable no-console */
import Vue from "vue";
import { ConfigModule } from "@/store/modules/config";

let waitingRegistration: ServiceWorkerRegistration | null = null;

/**
 * Activate the waiting service worker and reload once it takes control.
 * Triggered by the user (system bar / settings button) instead of automatically,
 * so customers are not interrupted mid-action by an unsolicited refresh.
 */
export function applyUpdate(): void {
  const registration = waitingRegistration;
  if (!registration?.waiting) {
    window.location.reload();
    return;
  }
  // `{ once: true }` prevents listener accumulation if the user clicks
  // multiple times before the controller actually changes.
  navigator.serviceWorker.addEventListener(
    "controllerchange",
    () => {
      window.location.reload();
    },
    { once: true }
  );
  waitingRegistration = null;
  registration.waiting.postMessage({ type: "SKIP_WAITING" });
}

if (process.env.NODE_ENV === "production") {
  // Lazy require so Jest (which sets NODE_ENV=test) never has to transform
  // register-service-worker, an ESM-only package not in transformIgnorePatterns.
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const { register } = require("register-service-worker");

  /**
   * Reload the page once when a chunk fails to load (stale deploy).
   * Uses a sessionStorage flag to allow exactly one auto-reload per session;
   * the user must manually refresh to retry after that.
   */
  const RELOAD_KEY = "chunk-reload-attempted";

  const handleChunkLoadError = () => {
    if (!sessionStorage.getItem(RELOAD_KEY)) {
      sessionStorage.setItem(RELOAD_KEY, "1");
      window.location.reload();
    }
  };

  // Catch dynamic import / chunk load failures (e.g. stale deploy serving HTML for JS)
  window.addEventListener("unhandledrejection", (event) => {
    const error = event?.reason;
    if (
      error &&
      (error.name === "ChunkLoadError" ||
        (error instanceof SyntaxError && error.message?.includes("Unexpected token '<'")))
    ) {
      handleChunkLoadError();
    }
  });

  register(`${process.env.BASE_URL}service-worker.js`, {
    ready() {
      Vue.$log.info(
        "App is being served from cache by a service worker.\n" + "For more details, visit https://goo.gl/AFskqB"
      );
    },
    registered() {
      Vue.$log.info("Service worker has been registered.");
    },
    cached() {
      Vue.$log.info("Content has been cached for offline use.");
    },
    updatefound() {
      Vue.$log.info("New content is downloading.");
    },
    updated(registration: ServiceWorkerRegistration) {
      Vue.$log.info("New content is available; awaiting user confirmation to reload.");
      waitingRegistration = registration;
      ConfigModule.markUpdateAvailable();
    },
    offline() {
      Vue.$log.info("No internet connection found. App is running in offline mode.");
    },
    error(error: Error) {
      console.error("Error during service worker registration:", error);
    }
  });
}
